import * as React from 'react';

import { Button, Col, Label, Row, TextArea, cn } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideArrowUp,
  LucideMic,
  LucideMicOff,
  LucidePaperclip,
  LucideSparkle,
} from 'lucide-react';

import * as conversationState from '../../_state/chatConversation.state';
import * as inputState from '../../_state/chatInput.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AttachmentPreview } from './AttachmentPreview';
import { useAssistantFileUpload } from './hooks/useAssistantFileUpload';
import { useVoiceInput } from './hooks/useVoiceInput';

import { useIdentity } from '~/shared/hooks/useIdentity';

const SPARKLE_PREFIX = (
  <LucideSparkle size={16} className="mt-0.5 text-purple" />
);

interface MessageInputProps {
  autoFocus?: boolean;
}

//
// ⚛️ ⚛️ ⚛️ MESSAGE INPUT ⚛️ ⚛️ ⚛️
//
export const MessageInput = ({ autoFocus }: MessageInputProps = {}) => {
  const t = useTranslations();

  // Refs
  const textAreaRef = React.useRef<HTMLDivElement>(null);
  const handleSubmitRef = React.useRef<() => Promise<void>>(null);

  // States
  const [isDragOver, setIsDragOver] = React.useState(false);

  // Atoms
  const canSend = useAtomValue(inputState.$canSendMessage);
  const isSending = useAtomValue(conversationState.$isSendingMessage);
  const attachments = useAtomValue(inputState.$pendingAttachments);
  const [input, setInput] = useAtom(inputState.$messageInput);
  const sendMessage = useSetAtom(conversationState.$sendMessage);
  const clearAttachments = useSetAtom(inputState.$clearAttachments);

  // Data hooks
  const { user } = useIdentity();
  const { uploadFiles, removeAttachment } = useAssistantFileUpload();

  const confirmedAttachments = attachments.filter(
    (a) => a.status === 'confirmed' && a.fileId,
  );
  const confirmedFileIds = confirmedAttachments.map((a) => a.fileId as string);

  // Callbacks
  const handleSubmit = React.useCallback(async () => {
    const content = input.trim();
    if (!content && confirmedFileIds.length === 0) return;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const attachmentMeta = confirmedAttachments.map((a) => ({
      fileName: a.file.name,
      mimeType: a.file.type || 'application/octet-stream',
    }));

    setInput('');
    clearAttachments();
    await sendMessage({
      content: content || ' ',
      fileIds: confirmedFileIds.length > 0 ? confirmedFileIds : undefined,
      attachments: attachmentMeta.length > 0 ? attachmentMeta : undefined,
      firstName: user?.firstName,
      lastName: user?.lastName,
      timezone,
    });
  }, [
    input,
    confirmedFileIds,
    confirmedAttachments,
    setInput,
    clearAttachments,
    sendMessage,
    user,
  ]);

  handleSubmitRef.current = handleSubmit;

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && canSend) {
        e.preventDefault();
        handleSubmitRef.current?.();
      }
    },
    [canSend],
  );

  const focusTextArea = React.useCallback(() => {
    textAreaRef.current?.querySelector('textarea')?.focus();
  }, []);

  const handleVoiceTranscript = React.useCallback(
    (text: string) => {
      setInput(
        (prev) => prev + (prev && !prev.endsWith(' ') ? ' ' : '') + text,
      );
      focusTextArea();
    },
    [setInput, focusTextArea],
  );

  const handleVoiceSilenceTimeout = React.useCallback(() => {
    handleSubmitRef.current?.();
  }, []);

  const { isRecording, isSupported, startRecording, stopRecording } =
    useVoiceInput({
      onTranscript: handleVoiceTranscript,
      onSilenceTimeout: handleVoiceSilenceTimeout,
    });

  const handleFileInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        uploadFiles(e.target.files);
        e.target.value = '';
      }
    },
    [uploadFiles],
  );

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        uploadFiles(e.dataTransfer.files);
      }
    },
    [uploadFiles],
  );

  return (
    <div
      className={cn(
        '@MessageInput m-4 rounded-md bg-neutral/5 p-2',
        isDragOver && 'ring-2 ring-purple/40',
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Col gap2 yEnd>
        <div className="w-full flex-1 px-3 py-2">
          <TextArea
            ref={textAreaRef}
            value={input}
            autoFocus={autoFocus}
            onChange={setInput}
            onKeyDown={handleKeyDown}
            placeholder={t.input.placeholder}
            variant="bare"
            autoResize
            maxHeight={120}
            prefix={SPARKLE_PREFIX}
          />
        </div>
        <Row xBetween yCenter className="w-full">
          <Row gap2 yCenter className="flex-wrap">
            <Label className="flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-xs text-neutral/60 hover:bg-neutral/10 hover:text-neutral/80">
              <input
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="sr-only"
              />
              <LucidePaperclip size={14} />
              {t.input.addAttachment}
            </Label>
            <AttachmentPreview onRemove={removeAttachment} />
          </Row>
          <Row yCenter gap2>
            {isSupported && (
              <Button
                onPress={isRecording ? stopRecording : startRecording}
                size="icon"
                variant={isRecording ? 'solid' : 'outline'}
                color={isRecording ? 'failure' : 'neutral'}
                className={cn(isRecording && 'animate-pulse')}
                icon={isRecording ? <LucideMicOff /> : <LucideMic />}
              />
            )}
            <Button
              onPress={handleSubmit}
              isDisabled={!canSend}
              size="small"
              variant="solid"
              color="purple"
              className="pt-4 pb-4 shadow"
            >
              <LucideArrowUp size={16} />
              <span className="rounded-sm bg-white/10 p-1 text-xs">
                {isSending ? t.input.sending : t.input.submitKey}
              </span>
            </Button>
          </Row>
        </Row>
      </Col>
    </div>
  );
};
